/**
 * Audit Activities Data Hook
 *
 * Fetches audit activities and adapts them to the ActivityEvent shape
 * consumed by the ActivityFeed UI component via a config-driven transformer.
 */

import { api } from "convex/_generated/api";
import { useWorkspaceQuery } from "@/hooks";
import { useMemo } from "react";
import type {
  AuditActivity,
  AuditActivityType,
  AuditId,
} from "@/packages/types";
import type { ActivityEvent } from "@/components/ui/ActivityFeed/types";
import type { EventTypeRenderer } from "@/hooks/activityConfigs/types";
import { createActivityTransformer } from "./activityHelpers";
import { auditActivityConfig } from "./activityConfigs/audit-config";
import type { RawActivity } from "./activityConfigs/types";

const auditActivityToEvent = createActivityTransformer(auditActivityConfig);

// ─── Public adapter (exported for external use) ───────────────────────────────

export function auditActivityToEventFn(activity: AuditActivity): ActivityEvent {
  return auditActivityToEvent(activity as RawActivity<AuditActivityType>);
}

// ─── useAuditActivities ───────────────────────────────────────────────────────

interface UseAuditActivitiesReturn {
  activities: ActivityEvent[];
  isLoading: boolean;
  /** Field label lookup for diff rows — pass to ActivityFeed's fieldLabels prop */
  fieldLabels: Record<string, string>;
  /** Per-event-type renderers — pass to ActivityFeed's eventTypeRenderers prop */
  eventTypeRenderers: Record<string, EventTypeRenderer> | undefined;
}

export function useAuditActivities(
  auditId: AuditId | null,
): UseAuditActivitiesReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.audit_queries.getAuditActivities,
    auditId ? { auditId } : "skip",
  );

  const activities = useMemo<ActivityEvent[]>(() => {
    if (!data) return [];
    return (data as RawActivity<AuditActivityType>[]).map(auditActivityToEvent);
  }, [data]);

  return {
    activities,
    isLoading,
    fieldLabels: auditActivityConfig.fieldLabels,
    eventTypeRenderers: auditActivityConfig.eventTypeRenderers as
      | Record<string, EventTypeRenderer>
      | undefined,
  };
}
